(() => {
  if (customElements.get('patchistry-expression-social')) return;

  class PatchistryExpressionSocial extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.rail = this.querySelector('[data-social-rail]');
      if (!this.rail) return;
      this.active = true;
      this.cards = Array.from(this.querySelectorAll('[data-social-card]'));
      this.previous = this.querySelector('[data-social-previous]');
      this.next = this.querySelector('[data-social-next]');
      this.controls = this.querySelector('[data-social-controls]');
      this.pending = new Map();
      this.scrollFrame = 0;
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.updateControls();
        });
      };
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.onResize = () => this.updateControls();
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.rail);
        if (this.cards[0]) this.resizeObserver.observe(this.cards[0]);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.imageObserver.unobserve(entry.target);
            this.loadCard(entry.target);
          });
        }, { rootMargin: '400px 160px', threshold: 0.01 });
        this.cards.forEach((card) => {
          if (card.dataset.state !== 'ready') this.imageObserver.observe(card);
        });
      } else {
        this.cards.forEach((card) => this.loadCard(card));
      }

      this.updateControls();
    }

    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      this.imageObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.rail.removeEventListener('scroll', this.onScroll);
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.scrollFrame);
      this.pending.forEach(({ controller, timer }) => {
        clearTimeout(timer);
        controller.abort();
      });
      this.pending.clear();
    }

    updateControls() {
      if (!this.active) return;
      const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const position = Math.max(0, Math.min(maximum, this.rail.scrollLeft));
      if (this.controls) this.controls.hidden = maximum <= 2;
      if (this.previous) this.previous.disabled = position <= 2;
      if (this.next) this.next.disabled = position >= maximum - 2;
    }

    move(direction) {
      if (!this.cards.length) return;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const step = this.cards[0].getBoundingClientRect().width + gap;
      const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const left = Math.max(0, Math.min(maximum, this.rail.scrollLeft + step * direction));
      this.rail.scrollTo({
        left,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    creatorFrom(data) {
      if (typeof data.author_url === 'string') {
        try {
          const match = new URL(data.author_url).pathname.match(/^\/@([^/]+)/);
          if (match) return '@' + decodeURIComponent(match[1]);
        } catch (error) {}
      }
      return '';
    }

    updateCreator(card, data) {
      const creator = this.creatorFrom(data);
      if (!creator) return;
      const caption = card.querySelector('[data-social-creator]');
      if (caption) caption.textContent = creator;
      if (this.dataset.watchTemplate) {
        card.setAttribute('aria-label', this.dataset.watchTemplate.replace('__CREATOR__', creator));
      }
    }

    imageReady(image, source, signal) {
      return new Promise((resolve, reject) => {
        const cleanup = () => {
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          signal.removeEventListener('abort', onAbort);
        };
        const onLoad = () => {
          cleanup();
          if (image.naturalWidth > 0) resolve();
          else reject(new Error('Empty image'));
        };
        const onError = () => {
          cleanup();
          reject(new Error('Image unavailable'));
        };
        const onAbort = () => {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
        };
        if (signal.aborted) {
          onAbort();
          return;
        }
        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onError, { once: true });
        signal.addEventListener('abort', onAbort, { once: true });
        image.hidden = false;
        image.loading = 'eager';
        if (source) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = source;
        }
        if (image.complete) {
          if (image.naturalWidth > 0) onLoad();
          else if (image.getAttribute('src')) onError();
        }
      });
    }

    async loadCard(card) {
      if (!this.active || this.pending.has(card) || card.dataset.state === 'ready') return;
      const image = card.querySelector('.pes__image');
      if (!image || !card.dataset.videoUrl) {
        card.dataset.state = 'fallback';
        return;
      }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const operation = { controller, timer };
      this.pending.set(card, operation);
      card.dataset.state = 'loading';

      try {
        let imageLoaded = false;
        if (image.getAttribute('src')) {
          try {
            await this.imageReady(image, null, controller.signal);
            imageLoaded = true;
          } catch (error) {
            if (controller.signal.aborted) throw error;
          }
        }

        if (!imageLoaded) {
          const response = await fetch('https://www.tiktok.com/oembed?url=' + encodeURIComponent(card.dataset.videoUrl), {
            signal: controller.signal,
            credentials: 'omit'
          });
          if (!response.ok) throw new Error('Video metadata unavailable');
          const data = await response.json();
          if (!data || typeof data !== 'object') throw new Error('Invalid video metadata');
          this.updateCreator(card, data);
          if (typeof data.thumbnail_url !== 'string' || !data.thumbnail_url) {
            throw new Error('Video thumbnail unavailable');
          }
          const thumbnail = new URL(data.thumbnail_url);
          if (thumbnail.protocol !== 'https:') throw new Error('Invalid thumbnail URL');
          await this.imageReady(image, thumbnail.href, controller.signal);
        }

        if (this.active && this.pending.get(card) === operation) card.dataset.state = 'ready';
      } catch (error) {
        if (this.active && this.pending.get(card) === operation) {
          card.dataset.state = 'fallback';
          image.hidden = true;
          image.removeAttribute('src');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
        }
      } finally {
        clearTimeout(timer);
        if (this.pending.get(card) === operation) this.pending.delete(card);
      }
    }
  }

  customElements.define('patchistry-expression-social', PatchistryExpressionSocial);
})();
